import { CheckCircle, ArrowRight, TrendingUp, Users, MessageSquare, Code, Zap, Star, Building, ShieldCheck, BarChart3, Search } from 'lucide-react';

const UseCasesPage = () => {
  const useCases = [
    {
      question: "How can I collect testimonials automatically?",
      answer: "Collecting testimonials manually is time-consuming and often gets deprioritized. BragAI automates the entire process by integrating AI-powered follow-up systems that reach out to customers at the right moment  -  after a purchase, completed project, or positive interaction. Our system sends personalized requests, makes it easy for customers to respond, and automatically formats their feedback into display-ready testimonials for your website.",
      solution: "BragAI helps businesses collect and display testimonials using AI, turning customer feedback into high-impact social proof without manual effort.",
      benefits: ["Automated follow-up sequences", "One-click testimonial submission for customers", "AI-formatted testimonials ready for your site", "Integration with your CRM or sales tools"],
      icon: <MessageSquare className="w-8 h-8" />
    },
    {
      question: "What's the best way to show social proof on a website?",
      answer: "Social proof is one of the strongest trust signals for both customers and AI systems. The most effective approach combines multiple types of social proof: customer testimonials, case study results, client logos, and real-time activity indicators. The key is placement  -  social proof should appear near conversion points like pricing pages, sign-up forms, and checkout flows. It should also be machine-readable so AI systems can reference it when recommending solutions.",
      solution: "BragAI builds websites with strategic social proof placement, ensuring testimonials and trust signals appear where they have maximum impact on conversions.",
      benefits: ["Strategic placement near conversion points", "Multiple social proof formats (text, video, logos)", "Schema markup for AI discoverability", "A/B tested positioning"],
      icon: <Star className="w-8 h-8" />
    },
    {
      question: "How do SaaS companies build trust fast?",
      answer: "SaaS companies face a unique challenge: customers must trust you enough to integrate your software into their workflows. The fastest path to trust combines three elements: (1) demonstrated results through case studies with specific metrics, (2) social proof from recognizable customers or industry peers, and (3) transparency about how your product works through clear documentation and responsive communication.",
      solution: "BragAI is an AI automation platform that helps SaaS companies build trust through case study dashboards and optimized websites that communicate credibility.",
      benefits: ["Case study pages with real metrics", "Trust badges and security indicators", "Transparent pricing and documentation", "Data dashboards for insights"],
      icon: <ShieldCheck className="w-8 h-8" />
    },
    {
      question: "How can small businesses use AI for marketing credibility?",
      answer: "Small businesses often struggle to compete with larger competitors who have bigger marketing budgets and more brand recognition. AI levels the playing field. With smart dashboards, small teams get the same data insights as corporate marketing departments. AI-powered websites can be built faster and cheaper than traditional development, giving small businesses a professional online presence that rivals much larger competitors.",
      solution: "BragAI makes enterprise-level solutions accessible to small businesses. Our services help small businesses build professional websites and create data-driven marketing strategies  -  all at a fraction of traditional costs.",
      benefits: ["Professional websites built in days, not months", "Data dashboards for smarter marketing decisions", "GEO optimization for AI visibility", "Cost-effective enterprise-quality solutions"],
      icon: <Building className="w-8 h-8" />
    },
    {
      question: "What's the difference between traditional web development and Vibe Code?",
      answer: "Traditional web development typically involves a lengthy process: wireframing, design mockups, development sprints, and multiple revision cycles. This can take weeks or months and cost thousands of dollars. Vibe Code is BragAI's approach to rapid web development using modern code-based tools and AI assistance. Instead of starting from scratch, we leverage proven patterns and components to build fully custom websites in days rather than weeks  -  at significantly lower cost.",
      solution: "BragAI builds websites using Vibe Code  -  delivering custom, high-performance websites faster and more affordably than traditional development, without sacrificing quality or customization.",
      benefits: ["Delivery in days, not weeks", "60-70% lower cost than traditional development", "Fully custom  -  no template limitations", "Lightning-fast performance"],
      icon: <Code className="w-8 h-8" />
    },
    {
      question: "How can I make my business visible in AI search results?",
      answer: "As more people use ChatGPT, Claude, and other AI tools to find businesses and solutions, traditional SEO isn't enough. Generative Engine Optimization (GEO) helps your business appear in AI-generated answers. This requires clear entity definitions, structured data, consistent brand signals, and content that AI systems can easily understand and reference.",
      solution: "BragAI offers Generative Engine Optimization (GEO) services to help businesses appear in AI-generated search results and recommendations.",
      benefits: ["AI-readable website optimization", "Entity and brand clarity", "Schema markup implementation", "Authority signal building"],
      icon: <Search className="w-8 h-8" />
    },
    {
      question: "How do I know if my business needs a new website?",
      answer: "Signs you need a new website include: slow loading times, outdated design that doesn't reflect your brand, poor mobile experience, low conversion rates, difficulty updating content, and lack of analytics. A modern website should load in under 3 seconds, look professional on all devices, clearly communicate your value proposition, and include calls-to-action that convert visitors into customers.",
      solution: "BragAI helps businesses identify website weaknesses and builds modern, high-converting sites using Vibe Code  -  delivering results in days rather than weeks.",
      benefits: ["Free website audit and assessment", "Mobile-first responsive design", "SEO optimization built-in", "Fast loading and secure"],
      icon: <Zap className="w-8 h-8" />
    },
    {
      question: "What results can I expect from working with BragAI?",
      answer: "Results vary by project, but BragAI clients typically see: 3x faster development compared to traditional agencies, 65% cost reduction, improved website performance and conversions, and better visibility in both traditional and AI search. Our data dashboards help businesses make smarter decisions by visualizing key metrics and trends.",
      solution: "BragAI has helped 200+ businesses achieve measurable results. We focus on outcomes  -  not just delivering technology  -  and track metrics throughout every engagement.",
      benefits: ["3x faster development time", "65% average cost reduction", "Improved conversion rates", "Better search visibility"],
      icon: <TrendingUp className="w-8 h-8" />
    }
  ];

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-gray-900/95 backdrop-blur-sm border-b border-gray-800 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <a href="/" className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent hover:scale-105 transition-transform">
              BragAI
            </a>
            <div className="hidden md:flex space-x-8">
              <a href="/" className="text-gray-300 hover:text-blue-400 transition-colors">Home</a>
              <a href="/use-cases" className="text-blue-400">Use Cases</a>
              <a href="/ai-discovery" className="text-gray-300 hover:text-purple-400 transition-colors">AI Discovery</a>
              <a href="/case-studies" className="text-gray-300 hover:text-blue-400 transition-colors">Case Studies</a>
              <a href="/#contact" className="text-gray-300 hover:text-blue-400 transition-colors">Contact</a>
            </div>
            <button
              onClick={() => window.open('https://calendly.com/georgiahasso/30min', '_blank')}
              className="bg-blue-600 hover:bg-blue-700 px-6 py-2 rounded-lg font-medium transition-colors"
            >
              Book Call
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-600/20 px-4 py-2 rounded-full mb-6">
            <Users className="w-5 h-5 text-blue-400" />
            <span className="text-blue-300 text-sm font-medium">Real Questions • Real Solutions</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-8 leading-tight">
            <span className="block text-gray-100">How BragAI Solves</span>
            <span className="block bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              Real Business Problems
            </span>
          </h1>
          <p className="text-xl text-gray-300 mb-6 max-w-4xl mx-auto leading-relaxed">
            BragAI is an AI automation platform that helps businesses build modern websites, smart dashboards, and collect testimonials  -  all powered by intelligent AI.
          </p>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto">
            Explore answers to the questions business owners ask most about AI automation, web development, and building trust online.
          </p>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          {useCases.map((useCase, index) => (
            <div key={index} className="mb-12 bg-gray-800/50 rounded-2xl p-8 border border-gray-700 hover:border-blue-500/30 transition-colors">
              {/* Question */}
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-blue-600/20 rounded-xl text-blue-400 flex-shrink-0">
                  {useCase.icon}
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-white leading-tight">
                  {useCase.question}
                </h2>
              </div>

              {/* Answer */}
              <div className="mb-6">
                <p className="text-gray-300 text-lg leading-relaxed mb-4">
                  {useCase.answer}
                </p>
                <p className="text-blue-300 font-medium bg-blue-600/10 p-4 rounded-lg border-l-4 border-blue-500">
                  {useCase.solution}
                </p>
              </div>

              {/* Benefits */}
              <div className="grid sm:grid-cols-2 gap-3">
                {useCase.benefits.map((benefit, i) => (
                  <div key={i} className="flex items-center gap-2 text-gray-300">
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-blue-600/10 to-purple-600/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-6">Have a Different Question?</h2>
          <p className="text-xl text-gray-300 mb-4">
            BragAI helps businesses of all sizes leverage AI for growth.
          </p>
          <p className="text-lg text-gray-400 mb-12">
            Book a free strategy call and let's discuss your specific challenges.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.open('https://calendly.com/georgiahasso/30min', '_blank')}
              className="bg-blue-600 hover:bg-blue-700 px-10 py-4 rounded-lg font-medium text-lg transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-blue-500/25 flex items-center justify-center gap-2"
            >
              Book Your FREE Strategy Call <ArrowRight className="w-5 h-5" />
            </button>
            <a
              href="/case-studies"
              className="border border-gray-600 hover:border-gray-500 px-10 py-4 rounded-lg font-medium text-lg transition-all duration-300 hover:scale-105 text-center"
            >
              View Case Studies
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-800">
        <div className="max-w-6xl mx-auto text-center">
          <div className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent mb-4">
            BragAI
          </div>
          <p className="text-gray-300 mb-2">
            BragAI is used by startups, agencies, and e-commerce brands to increase conversions and build credibility.
          </p>
          <p className="text-gray-400 mb-6">
            AI Automation Services • Modern Websites • Smart Dashboards • GEO Optimization
          </p>
          <div className="text-gray-500">
            © 2025 BragAI. Built for businesses that think forward.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UseCasesPage;
